import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { buildSeo } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { Eyebrow, MarketingLayout, I18nPageHero } from "@/components/marketing/Layout";
import { submitContactInquiry } from "@/lib/contact.functions";
import { Check } from "lucide-react";

export const Route = createFileRoute("/contact")({
  head: () => {
    const seo = buildSeo({
      path: "/contact",
      title: "Contact — Reality Twin (Ruznic Marketing, Hinwil)",
      description:
        "Get in touch with the team behind Reality Twin. We're a small studio in Hinwil, Switzerland — Ruznic Marketing, owned by Ajdin Ruznic.",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            url: "https://realitytwin.io/contact",
            name: "Contact Reality Twin",
            mainEntity: {
              "@type": "Organization",
              name: "Ruznic Marketing",
              email: "hello@realitytwin.io",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Leimgrubstrasse 5",
                postalCode: "8340",
                addressLocality: "Hinwil",
                addressCountry: "CH",
              },
            },
          }),
        },
      ],
    };
  },
  component: ContactPage,
});

const EMAIL = "hello@realitytwin.io";

function ContactPage() {
  const { t } = useTranslation();
  const channels = t("routes.contact.channels", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];
  const offices = t("routes.contact.offices", { returnObjects: true }) as {
    city: string;
    address: string;
  }[];
  const placeholders = t("routes.contact.placeholders", { returnObjects: true }) as {
    name: string;
    email: string;
    company: string;
    message: string;
  };
  const teamOptions = t("routes.contact.teamOptions", { returnObjects: true }) as string[];

  const sendInquiry = useServerFn(submitContactInquiry);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    team: teamOptions[0] ?? "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    setError(null);
    setSending(true);
    try {
      await sendInquiry({
        data: {
          name: form.name,
          email: form.email,
          company: form.company || undefined,
          team: form.team || undefined,
          message: form.message,
        },
      });
      setSent(true);
    } catch (err: any) {
      setError(err?.message ?? "Could not send — please email us directly.");
    } finally {
      setSending(false);
    }
  }
  return (
    <MarketingLayout>
      <I18nPageHero tKey="pages.contact" />

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-[1fr_1.2fr] gap-10">
          <div>
            <Eyebrow>{t("routes.contact.channelsLabel")}</Eyebrow>
            <div className="mt-6 grid sm:grid-cols-2 gap-px bg-border hairline rounded-xl overflow-hidden">
              {channels.map((c) => (
                <a
                  key={c.title}
                  href={`mailto:${EMAIL}`}
                  className="bg-card p-6 hover:bg-cream transition"
                >
                  <div className="font-serif text-[22px] leading-tight">{c.title}</div>
                  <div className="mt-2 text-[13px] text-muted-foreground">{c.desc}</div>
                  <div className="mt-4 text-[13px] underline">{EMAIL}</div>
                </a>
              ))}
            </div>

            <Eyebrow>
              <span className="block mt-12">{t("routes.contact.officesLabel")}</span>
            </Eyebrow>
            <div className="mt-6 hairline rounded-xl bg-card divide-y divide-border">
              {offices.map((o) => (
                <div key={o.city} className="px-6 py-5">
                  <div className="font-serif text-[20px] leading-tight">{o.city}</div>
                  <div className="mt-1.5 text-[13px] text-muted-foreground whitespace-pre-line">
                    {o.address}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {sent ? (
            <div className="hairline rounded-xl bg-card p-10 flex flex-col items-center justify-center text-center">
              <div className="h-12 w-12 rounded-full bg-foreground text-background flex items-center justify-center">
                <Check className="h-6 w-6" />
              </div>
              <h2 className="mt-5 font-serif text-[28px] leading-tight">Message sent</h2>
              <p className="mt-2 text-[14px] text-muted-foreground max-w-sm">
                Thanks {form.name.split(" ")[0]} — we read every message and reply within one
                business day to {form.email}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="hairline rounded-xl bg-card p-7">
              <Eyebrow>{t("routes.contact.formEyebrow")}</Eyebrow>
              <h2 className="mt-3 font-serif text-[32px] leading-tight">
                {t("routes.contact.formTitle")}
              </h2>
              <div className="mt-7 space-y-4">
                <Field label={t("routes.contact.fieldName")}>
                  <input
                    className="field"
                    placeholder={placeholders.name}
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Field>
                <Field label={t("routes.contact.fieldEmail")}>
                  <input
                    className="field"
                    placeholder={placeholders.email}
                    type="email"
                    required
                    maxLength={200}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Field>
                <Field label={t("routes.contact.fieldCompany")}>
                  <input
                    className="field"
                    placeholder={placeholders.company}
                    maxLength={120}
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                  />
                </Field>
                <Field label={t("routes.contact.fieldTeam")}>
                  <select
                    className="field"
                    value={form.team}
                    onChange={(e) => setForm({ ...form, team: e.target.value })}
                  >
                    {teamOptions.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t("routes.contact.fieldMessage")}>
                  <textarea
                    className="field min-h-[110px] resize-y"
                    placeholder={placeholders.message}
                    required
                    minLength={10}
                    maxLength={2000}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </Field>
              </div>
              {error && <p className="mt-3 text-[13px] text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={sending}
                className="mt-6 inline-flex justify-center items-center rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition w-full sm:w-auto disabled:opacity-50"
              >
                {sending ? "Sending…" : t("routes.contact.sendCta")}
              </button>
              <p className="mt-3 text-[12px] text-muted-foreground">
                {t("routes.contact.legalPre")}
                <a href="/privacy" className="underline">
                  {t("routes.contact.legalLink")}
                </a>
                {t("routes.contact.legalPost")}
              </p>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .field { width: 100%; background: var(--color-background); border: 1px solid var(--color-border); border-radius: 8px; padding: 10px 12px; font-size: 14px; color: var(--color-foreground); font-family: inherit; }
        .field:focus { outline: none; border-color: var(--color-foreground); }
        .field::placeholder { color: var(--color-muted-foreground); opacity: 0.6; }
      `}</style>
    </MarketingLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-[12.5px] text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
